import { useRef } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Overline, SectionTitle } from "./Reveal";
import { Phone } from "./Phone";

const ITEMS = [
  ["Workout", "Today's session, ready to start", "app-workout"],
  ["Week", "Your full training split", "app-week"],
  ["Plan", "Same workouts, personal weights", "app-plan"],
  ["Progress", "Weekly weigh-ins and progress photos", "app-progress"],
  ["Meals", "Calories and macros diary", "app-meals"],
  ["Fridge", "Scan a product, log it in one tap", "app-fridge"],
  ["Recipes", "Calories per serve, worked out for you", "app-recipe"],
  ["Water", "Daily water and steps", "app-water"],
  ["Calories", "A daily goal that updates as you change", "app-calories"],
  ["Goal", "Pick the body you're chasing", "app-goal"],
];
const OFFSETS = [[20, -20], [50, -50], [30, -30], [60, -60], [25, -25], [55, -55], [35, -35], [45, -45], [25, -25], [50, -50]];

const Arrow = ({ dir, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`screens-${dir}-btn`} aria-label={dir} className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 transition-[background-color,border-color] duration-300 hover:border-[#ff6a3d]/60 hover:bg-[#ff6a3d]/10">
    {dir === "prev" ? <ArrowLeft className="h-5 w-5" /> : <ArrowRight className="h-5 w-5" />}
  </button>
);

export const Screens = () => {
  const ref = useRef(null);
  const rail = useRef(null);
  const slide = (d) => rail.current?.scrollBy({ left: d * 300, behavior: "smooth" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y0 = useTransform(scrollYProgress, [0, 1], OFFSETS[0]);
  const y1 = useTransform(scrollYProgress, [0, 1], OFFSETS[1]);
  const y2 = useTransform(scrollYProgress, [0, 1], OFFSETS[2]);
  const y3 = useTransform(scrollYProgress, [0, 1], OFFSETS[3]);
  const y4 = useTransform(scrollYProgress, [0, 1], OFFSETS[4]);
  const y5 = useTransform(scrollYProgress, [0, 1], OFFSETS[5]);
  const y6 = useTransform(scrollYProgress, [0, 1], OFFSETS[6]);
  const y7 = useTransform(scrollYProgress, [0, 1], OFFSETS[7]);
  const y8 = useTransform(scrollYProgress, [0, 1], OFFSETS[8]);
  const y9 = useTransform(scrollYProgress, [0, 1], OFFSETS[9]);
  const ys = [y0, y1, y2, y3, y4, y5, y6, y7, y8, y9];

  return (
    <section id="screens" ref={ref} className="grain relative overflow-hidden py-24 lg:py-36">
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[600px] w-[900px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#ff6a3d]/10 blur-[160px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex items-end justify-between gap-6">
          <Reveal className="max-w-3xl"><Overline>Inside the app</Overline><SectionTitle className="mt-4">Built for the <span className="text-grad">gym floor.</span></SectionTitle></Reveal>
          <div className="hidden gap-3 md:flex"><Arrow dir="prev" onClick={() => slide(-1)} /><Arrow dir="next" onClick={() => slide(1)} /></div>
        </div>
        <div ref={rail} data-testid="screens-rail" className="no-scrollbar -mx-5 mt-8 flex snap-x snap-mandatory gap-6 overflow-x-auto px-5 py-16 sm:-mx-8 sm:px-8">
          {ITEMS.map(([t, d, img], i) => (
            <motion.figure key={t} style={{ y: ys[i] }} className="snap-center" data-testid={`screen-${t.toLowerCase()}`}>
              <Phone src={`/images/${img}.webp`} alt={`Spotter app ${t} screen`} className="xl:w-[240px]" />
              <figcaption className="mt-6">
                <p className="font-display text-2xl font-extrabold uppercase">{t}</p>
                <p className="text-sm text-[#b3b8c1]">{d}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
